import { useEffect, useState, useCallback } from 'react';
import { handleSuccess, isNativePlatform } from 'services/shared/clientService';
import { toaster } from 'components/toaster';
import { createPurchase } from 'services/shared/purchaseService';
import * as Sentry from '@sentry/nextjs';
import { PurchaseCreateInterface } from 'schemas/purchaseCreateSchema';
import { getCreditPackets } from 'services/shared/creditService';
import { Transaction } from 'capacitor-plugin-cdv-purchase';

interface IAPResponse {
	products: CdvPurchase.Product[];
	isOwned: boolean;
	makePurchase: (productId: string) => void;
}

interface StoreState {
	get: (id: string) => CdvPurchase.Product | null;
	products: CdvPurchase.Product[];
}

interface TransactionFull extends Transaction {
	jwsRepresentation: string;
}

export function useIAP(userId: number | undefined): IAPResponse {
	const [store, setStore] = useState<StoreState | CdvPurchase.Store | null>(null);
	const [products, setProducts] = useState<CdvPurchase.Product[]>([]);
	const [isOwned, setIsOwned] = useState<boolean>(false);

	useEffect(() => {
		if (!userId) return;

		const initStore = async () => {
			const { CdvPurchase, store: iapStore } = await import('capacitor-plugin-cdv-purchase');

			if (!isNativePlatform()) {
				const mockProducts: any[] = [
					{
						id: '500_credits',
						title: 'Starter Pack (500 Credits)',
						description: 'Generate up to 62 HD images',
						pricing: { price: '$5.00' },
						offers: [
							{
								id: 'default',
								pricingPhases: [{ price: '$5.00' }],
								order: () => {
									console.log('Mock purchase triggered');
									alert('Mock Purchase Success!');
								},
							},
						],
						getOffer: function () {
							return this.offers[0];
						},
						order: function () {
							this.offers[0].order();
						},
					},
				];

				setProducts(mockProducts as unknown as CdvPurchase.Product[]);
				setStore({
					get: (id: string) => mockProducts.find((p) => p.id === id) || null,
					products: mockProducts as unknown as CdvPurchase.Product[],
				});
				return;
			}

			// Register Products
			iapStore.register([
				{
					id: process.env.NEXT_PUBLIC_APPLE_STARTER_PRODUCT_ID || '',
					platform: CdvPurchase.Platform.APPLE_APPSTORE,
					type: CdvPurchase.ProductType.CONSUMABLE,
				},
				{
					id: process.env.NEXT_PUBLIC_APPLE_PRO_PRODUCT_ID || '',
					platform: CdvPurchase.Platform.APPLE_APPSTORE,
					type: CdvPurchase.ProductType.CONSUMABLE,
				},
				{
					id: process.env.NEXT_PUBLIC_APPLE_TURBO_PRODUCT_ID || '',
					platform: CdvPurchase.Platform.APPLE_APPSTORE,
					type: CdvPurchase.ProductType.CONSUMABLE,
				},
			]);

			iapStore
				.when()
				.productUpdated((p: CdvPurchase.Product) => {
					setProducts([...iapStore.products]);
				})
				.approved((transaction: CdvPurchase.Transaction) => {
					transaction.verify();
				})
				.verified(async (receipt: CdvPurchase.VerifiedReceipt) => {
					try {
						const purchase =
							receipt.collection[receipt.collection.length - 1] ||
							receipt.sourceReceipt.transactions?.[receipt.sourceReceipt.transactions.length - 1];

						if (!purchase) {
							toaster.create({
								description: 'Purchase failed. Please try again.',
								type: 'error',
								closable: true,
							});
							await receipt.finish();
							return;
						}

						const transaction =
							receipt.sourceReceipt.transactions?.[receipt.sourceReceipt.transactions.length - 1];
						if (!transaction) {
							await receipt.finish();
							return;
						}

						const base64Receipt = (transaction as TransactionFull).jwsRepresentation || 'invalid';

						const productId =
							(purchase as any).id || (purchase as any).productId || (purchase as any).products?.[0]?.id;

						const creditPackets = getCreditPackets();
						const selectedCreditPacket = creditPackets.find(
							(packet) => packet.apple_product_id === productId
						);

						if (!selectedCreditPacket) {
							// eslint-disable-next-line no-console
							Sentry.captureException('Something went wrong. We will update your credits shortly.');
							toaster.create({
								description: 'Something went wrong. We will update your credits shortly.',
								type: 'error',
								closable: true,
							});
							await receipt.finish();
							return;
						}

						// create purchase
						const purchaseData: PurchaseCreateInterface = {
							platform: 'ios',
							user_id: userId,
							invoiceId: base64Receipt,
							creditpack_id: selectedCreditPacket.id,
						};
						const response = await createPurchase(purchaseData);

						if (response && response.result) {
							await receipt.finish();
							handleSuccess(`${selectedCreditPacket.credits} Credits have been added to your account.`);
						} else {
							Sentry.captureException('Purchase could not be created');
							console.error('Purchase could not be created');
							await receipt.finish();
						}
					} catch (error) {
						Sentry.captureException(error);
						console.error('IAP Processing Error:', error);
						await receipt.finish();
					}
				});

			iapStore.ready(() => {
				setProducts([...iapStore.products]);
			});

			try {
				await iapStore.initialize([CdvPurchase.Platform.APPLE_APPSTORE]);
				setStore(iapStore);
			} catch (err) {
				Sentry.captureException(err);
			}
		};

		initStore().catch(console.error);
	}, [userId]);

	const makePurchase = useCallback(
		async (productId: string) => {
			if (!store) {
				toaster.create({
					description: 'Store not initialized. Please try again.',
					type: 'error',
				});
				return;
			}

			const product = store.get(productId);

			if (product) {
				const offer = product.getOffer();
				if (offer) {
					offer.order();
				} else if (typeof (product as any).order === 'function') {
					(product as any).order();
				}
			} else {
				console.error('Product not found:', productId);
			}
		},
		[store]
	);

	return { products, isOwned, makePurchase };
}
